import React, { useEffect, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Checkbox, Input } from '@roger/ui-components';
import { UserCog } from 'lucide-react';
import { updateUser } from '../../../services/api';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  isRogerEmployee: z.boolean(),
});

type FormData = z.infer<typeof schema>;

export type EditUserInfoHandle = {
  saveIfDirty: () => Promise<void>;
};

type EditUserInfoSectionProps = {
  userId: string;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  isRogerEmployee: boolean;
};

export const EditUserInfoSection = React.memo(
  React.forwardRef<EditUserInfoHandle, EditUserInfoSectionProps>(
    function EditUserInfoSection(
      { userId, firstName, lastName, isRogerEmployee },
      ref
    ) {
      const { showError } = useStore(
        useShallow(state => ({ showError: state.showError }))
      );

      const {
        register,
        control,
        reset,
        trigger,
        getValues,
        formState: { errors, isDirty },
      } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          isRogerEmployee: isRogerEmployee,
        },
      });

      useEffect(() => {
        reset({
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          isRogerEmployee: isRogerEmployee,
        });
      }, [firstName, lastName, isRogerEmployee, reset]);

      useImperativeHandle(
        ref,
        () => ({
          saveIfDirty: async () => {
            if (!isDirty) return;
            const isValid = await trigger();
            if (!isValid) throw new Error('Validation failed');
            const data = getValues();
            try {
              await updateUser({
                path: { user_id: userId },
                body: {
                  first_name: data.firstName,
                  last_name: data.lastName,
                  is_roger_employee: data.isRogerEmployee,
                },
                throwOnError: true,
              });
            } catch (error) {
              console.error('Failed to update user info:', error);
              showError('Failed to update user info. Please try again.');
              throw error;
            }
          },
        }),
        [isDirty, trigger, getValues, userId, showError]
      );

      return (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <UserCog className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>User Info</h3>
          </div>
          <div className='grid grid-cols-2 gap-3 items-start'>
            <div className='space-y-1'>
              <label htmlFor='edit-firstName' className='text-xs font-medium'>
                First Name
              </label>
              <Input
                id='edit-firstName'
                inputSize='sm'
                {...register('firstName')}
                aria-invalid={errors.firstName ? 'true' : 'false'}
              />
              {errors.firstName && (
                <p className='text-xs text-red-600'>
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className='space-y-1'>
              <label htmlFor='edit-lastName' className='text-xs font-medium'>
                Last Name
              </label>
              <Input
                id='edit-lastName'
                inputSize='sm'
                {...register('lastName')}
                aria-invalid={errors.lastName ? 'true' : 'false'}
              />
              {errors.lastName && (
                <p className='text-xs text-red-600'>
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
          <Controller
            name='isRogerEmployee'
            control={control}
            render={({ field }) => (
              <label className='flex cursor-pointer items-center gap-2'>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={checked => field.onChange(checked === true)}
                  checkboxSize='sm'
                />
                <span className='text-sm'>Roger employee</span>
              </label>
            )}
          />
        </div>
      );
    }
  )
);
